import React from "react";
import type { NewsBlock } from "../../../types";
import { absoluteAssetUrl, sanitizeHtml } from "../../utils/utils";

const youtubeEmbed = (url: string) => {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtu.be")) return `https://www.youtube.com/embed/${parsed.pathname.slice(1)}`;
    const id = parsed.searchParams.get("v");
    if (id) return `https://www.youtube.com/embed/${id}`;
    return parsed.pathname.includes("/embed/") ? url : "";
  } catch {
    return "";
  }
};

const NewsBlockRenderer: React.FC<{ block: NewsBlock; device?: "desktop" | "mobile" }> = ({ block, device = "desktop" }) => {
  if ((device === "desktop" && block.responsive.hideOnDesktop) || (device === "mobile" && block.responsive.hideOnMobile)) return null;

  const style: React.CSSProperties = {
    textAlign: (block.settings.textAlign as React.CSSProperties["textAlign"]) || "left",
    background: String(block.settings.backgroundColor || "") || undefined,
    padding: Number(block.settings.padding || 0) || undefined,
    borderRadius: Number(block.settings.borderRadius || 0) || undefined,
  };

  if (block.type === "Heading") {
    const text = String(block.content.text || "");
    const level = Number(block.content.level || 2);
    if (level === 3) return <h3 className="news-render-heading" style={style}>{text}</h3>;
    if (level === 4) return <h4 className="news-render-heading" style={style}>{text}</h4>;
    return <h2 className="news-render-heading" style={style}>{text}</h2>;
  }

  if (block.type === "Text") {
    return <div className="news-render-richtext" style={style} dangerouslySetInnerHTML={{ __html: sanitizeHtml(String(block.content.html || "")) }} />;
  }

  if (block.type === "Image") {
    const src = absoluteAssetUrl(String(block.content.imageUrl || ""));
    if (!src) return null;
    return <figure className="news-render-image" style={style}>
      <img src={src} alt={String(block.content.alt || "")} />
      {block.content.caption ? <figcaption>{String(block.content.caption)}</figcaption> : null}
    </figure>;
  }

  if (block.type === "ImageText") {
    const src = absoluteAssetUrl(String(block.content.imageUrl || ""));
    const right = String(block.content.imagePosition || "left") === "right";
    return <section className={`news-render-image-text ${right ? "image-right" : ""}`} style={style}>
      <div className="news-render-image-text-media">{src ? <img src={src} alt={String(block.content.alt || "")} /> : <span>KJ</span>}</div>
      <div className="news-render-image-text-copy"><h3>{String(block.content.title || "")}</h3><p>{String(block.content.description || "")}</p></div>
    </section>;
  }

  if (block.type === "Quote") {
    return <blockquote className="news-render-quote" style={style}><p>“{String(block.content.quote || "")}”</p>{block.content.author ? <cite>{String(block.content.author)}</cite> : null}</blockquote>;
  }

  if (block.type === "Callout") {
    return <aside className={`news-render-callout tone-${String(block.content.tone || "info")}`} style={style}><strong>{String(block.content.title || "")}</strong><p>{String(block.content.text || "")}</p></aside>;
  }

  if (block.type === "Button") {
    const newTab = Boolean(block.content.openInNewTab);
    return <div className="news-render-button-wrap" style={style}><a className="news-render-button" href={String(block.content.url || "#")} target={newTab ? "_blank" : undefined} rel={newTab ? "noreferrer" : undefined}>{String(block.content.text || "Xem thêm")}</a></div>;
  }

  if (block.type === "Video") {
    const src = youtubeEmbed(String(block.content.url || ""));
    return src ? <div className="news-render-video" style={style}><iframe src={src} title={String(block.content.title || "Video")} allowFullScreen /></div> : null;
  }

  if (block.type === "Divider") return <hr className="news-render-divider" />;
  return null;
};

export default NewsBlockRenderer;
